import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  isPlausibleSplitInviteToken,
  PENDING_SPLIT_INVITE_TOKEN_KEY,
} from '../../constants/splitInvite';

type InvitePageState = 'loading' | 'continue' | 'error';

/**
 * Public route: captures opaque invite token from the email CTA, stores it in
 * sessionStorage only, cleans the URL, then continues into normal auth.
 */
const InviteLandingPage: React.FC = () => {
  const { token: routeToken } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<InvitePageState>('loading');
  const [errorMessage, setErrorMessage] = useState(
    'This invitation link is invalid or no longer available.'
  );

  useEffect(() => {
    const fromRoute = (routeToken || '').trim();
    let storedToken: string | null = null;
    try {
      storedToken = sessionStorage.getItem(PENDING_SPLIT_INVITE_TOKEN_KEY);
    } catch {
      storedToken = null;
    }

    // Prefer URL token (fresh click); fall back to same-tab session token after URL clean/refresh.
    const rawToken = isPlausibleSplitInviteToken(fromRoute)
      ? fromRoute
      : isPlausibleSplitInviteToken(storedToken)
        ? (storedToken as string)
        : '';

    if (!isPlausibleSplitInviteToken(rawToken)) {
      setErrorMessage('This invitation link is invalid or no longer available.');
      setPageState('error');
      // Clean malformed token from the visible URL without leaking it further.
      if (fromRoute) {
        window.history.replaceState({}, document.title, '/invite');
      }
      return;
    }

    try {
      sessionStorage.setItem(PENDING_SPLIT_INVITE_TOKEN_KEY, rawToken);
    } catch {
      setErrorMessage('Unable to save this invitation in your browser. Please try again.');
      setPageState('error');
      return;
    }

    // Remove usable token from history / screenshots / analytics URL capture.
    if (fromRoute) {
      window.history.replaceState({}, document.title, '/invite');
    }

    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      // Already authenticated — coordinator will reserve/finalize.
      setPageState('continue');
      navigate('/home', { replace: true });
      return;
    }

    setPageState('continue');
    navigate('/?openLogin=true', { replace: true });
  }, [routeToken, navigate]);

  if (pageState === 'error') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#0b1220',
          color: '#f5f7fb',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <h1 style={{ fontSize: 22, marginBottom: 12 }}>Invitation unavailable</h1>
          <p style={{ color: '#b7c0d1', lineHeight: 1.5, marginBottom: 20 }}>{errorMessage}</p>
          <p style={{ color: '#8b95a8', fontSize: 14 }}>
            If you believe this is a mistake, contact{' '}
            <a href="mailto:support@songplace.io" style={{ color: '#4da3ff' }}>
              support@songplace.io
            </a>
            .
          </p>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            style={{
              marginTop: 24,
              border: 0,
              borderRadius: 24,
              padding: '12px 28px',
              background: '#007aff',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to Songplace Curator
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0b1220',
        color: '#f5f7fb',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <p style={{ color: '#b7c0d1' }}>Continue to Songplace Curator…</p>
    </div>
  );
};

export default InviteLandingPage;
